import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Hammer, Cpu, Check,
  Briefcase, TrendingUp, Settings, ShieldCheck, Globe, Magnet,
  Workflow, BarChart3, Bot, Users, Building2, Boxes, Clock, FileText, PhoneCall, Handshake,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";
import AIAgentCTA from "@/components/AIAgentCTA";
import HeroBrandMark from "@/components/HeroBrandMark";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/6461294c-b3c3-4056-9836-e7bb045d737d/images/5399be7959bce57dadc69b64b7c83892449ebe74ba7561011f8b002cf0c40172.png";

const LEVERS = [
  { k: "Financials", v: "Cashflow, margin, capital structure, performance reporting." },
  { k: "Sales", v: "Pricing, sales process, conversion, pipeline architecture." },
  { k: "Operations", v: "SOPs, process design, outsourcing, admin systems." },
  { k: "Systems", v: "Website, lead capture, CRM, dashboards, AI, execution stack." },
];

const DIVISIONS = [
  {
    n: "01",
    icon: Globe,
    name: "Toulios Growth Systems",
    pitch: "Modern websites, lead capture, CRM, dashboards, AI support and an outsourced team, built and run for you.",
    note: "Flagship entry product · The Toulios Growth System",
    to: "/growth-system",
    flagship: true,
  },
  {
    n: "02",
    icon: TrendingUp,
    name: "Toulios Financial Strategy",
    pitch: "Cashflow, margin diagnostics, debt and capital strategy, financial structuring and performance reporting.",
    note: "Expansion offering",
    to: "/contact",
    flagship: false,
  },
  {
    n: "03",
    icon: Briefcase,
    name: "Toulios Revenue & Sales",
    pitch: "Pricing strategy, sales process design, conversion uplift, pipeline architecture and follow-up systems.",
    note: "Expansion offering",
    to: "/contact",
    flagship: false,
  },
  {
    n: "04",
    icon: Settings,
    name: "Toulios Operations",
    pitch: "Process design, SOPs, outsourcing models, operational efficiency and admin execution systems.",
    note: "Expansion offering",
    to: "/contact",
    flagship: false,
  },
];

const PROCESS = [
  { n: "01", title: "Diagnose", desc: "Senior advisor maps your numbers, operating model, and the single decision that matters most next." },
  { n: "02", title: "Design", desc: "A focused plan across the four levers, sequenced for momentum, not theatre." },
  { n: "03", title: "Deliver", desc: "We build it alongside your team. Model, dashboards, systems, processes, to production-grade." },
  { n: "04", title: "Run", desc: "Where it makes sense, our outsourced team keeps the system running. You get the gains, not the admin." },
];

const ARCHITECTURE = [
  {
    icon: Building2,
    role: "01 · Brand",
    label: "Toulios Advisory Group",
    desc: "The firm. Senior advisors who diagnose where you are, design where you go, and stay accountable end-to-end across financials, sales, operations and systems.",
    badge: "The advisor",
  },
  {
    icon: Boxes,
    role: "02 · System (offer)",
    label: "The Toulios Growth System",
    desc: "The engagement. A fixed-fee 60-day build that installs your operating model: six integrated components delivered as one connected system, then operated by us.",
    badge: "What we install",
  },
  {
    icon: Cpu,
    role: "03 · Product (software)",
    label: "Toulios Growth OS",
    desc: "The software. One branded platform that runs the system day-to-day: site, capture, CRM, dashboards, AI and ops, in a single login for you and your team.",
    badge: "What you sign into",
  },
];

const OS_HIGHLIGHTS = [
  "One branded platform, not five vendor logins.",
  "Real-time data across leads, jobs, revenue and margin.",
  "Built-in AI assistant trained on your business.",
  "Mobile and desktop, role-based access for your team.",
  "Your data stays yours: exportable, portable, owned.",
];

const OS_MODULES = [
  { id: "site", name: "Site", desc: "Conversion-focused website + local SEO foundation.", icon: Globe },
  { id: "capture", name: "Capture", desc: "Forms, calls, missed-call SMS, ad lead sync.", icon: Magnet },
  { id: "crm", name: "CRM", desc: "Job lifecycle, automated workflows, full history.", icon: Workflow },
  { id: "dash", name: "Dashboards", desc: "Real-time KPI cockpit, margin by job type.", icon: BarChart3 },
  { id: "ai", name: "AI", desc: "Chat assistant, after-hours voice receptionist.", icon: Bot },
  { id: "ops", name: "Ops", desc: "Command surface for your outsourced team.", icon: Users },
];

const PROOF = [
  { k: "Senior", v: "Operator-led, not delegated to juniors." },
  { k: "Integrated", v: "Advice + build + run, under one contract." },
  { k: "Accountable", v: "Outcomes-led, not deliverables-led." },
  { k: "Discreet", v: "Confidentiality is the baseline, not the upsell." },
];

const CASE_PREVIEWS = [
  { sector: "Plumbing · NSW", title: "+212% quote-to-job conversion in 4 months", note: "Growth System replaced brochure site + manual follow-up. 22 admin hours/week returned to the owner." },
  { sector: "Electrical · QLD", title: "Doubled monthly bookings, same ad spend", note: "Lead-capture rebuilt, response time down to 4 minutes. Same money in, twice the work out." },
  { sector: "Commercial Cleaning · VIC", title: "Replaced 3 part-time admins with one system", note: "Dashboards exposed two unprofitable contract types. Repricing alone paid back the system." },
];

const FAQS = [
  { q: "Are you a consultancy, an agency, or something else?", a: "We're a management consulting firm (ANZSIC M696299) that also installs and operates the systems we recommend. Not a deck factory. Not a web agency. Closer to an outsourced growth office for established operators." },
  { q: "Do I have to start with the Growth System?", a: "It's the most common entry point, the value is fastest to see and the ROI math is cleanest, but engagement can begin anywhere across the four divisions. The Strategy Call identifies the best place to start for your business." },
  { q: "How big does my business need to be?", a: "Most of our clients sit between $500k and $20M in annual revenue. Below $500k, the systems ROI math gets harder. Above $20M, we work alongside your existing leadership rather than replace it." },
  { q: "What do you charge?", a: "Fixed-fee for build / project work (quoted in writing after the Strategy Call), plus a transparent monthly fee for any ongoing run-mode or retained advisory. No equity, no commissions, no per-lead fees, no surprises." },
  { q: "Do you provide financial advice?", a: "No. Toulios is a management consulting firm. We do not hold an AFSL, do not provide brokerage or financial product advice, and do not deal in financial products. We help you operate a better business, your accountant and licensed adviser remain in their lanes." },
];

export default function Home() {
  return (
    <>
      {/* ===================== HERO ===================== */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-ink" />
        </div>

        <div className="relative container-x pt-24 md:pt-36 pb-24 md:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="overline" data-testid="hero-eyebrow">Toulios Advisory Group, Australia</div>
              </Reveal>

              <Reveal delay={120}>
                <h1
                  data-testid="hero-title"
                  className="mt-6 max-w-3xl font-display font-light tracking-tight text-balance text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02]"
                >
                  We <span className="text-gold">grow</span> with you.
                </h1>
              </Reveal>

              <Reveal delay={220}>
                <p className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed text-pretty">
                  A senior Australian advisory firm built around{" "}
                  <Link to="/pricing#grow-with-you" className="text-white underline-offset-4 hover:underline decoration-gold/60">
                    Grow-With-You
                  </Link>
                  : a reduced fixed base plus a performance share on
                  incremental gross profit, against a written baseline. Our
                  flagship engagement, The Toulios Growth System, is delivered
                  through our own software, <span className="text-gold">Toulios Growth OS</span>.
                </p>
              </Reveal>

              <Reveal delay={320}>
                <div className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="hero-ctas">
                  <AIAgentCTA testId="hero-cta-ai" />
                  <Link to="/contact" className="btn-outline-white" data-testid="hero-cta-book">
                    Book a Strategy Call
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <p className="mt-4 text-xs text-zinc-500 tracking-wide">
                  We earn more when you do. 5 to 15-minute AI analysis on your goals, then a senior consultant prepares your plan within 1 to 2 business days.
                </p>
                <p className="mt-2 text-[11px] text-zinc-600 tracking-wide leading-relaxed max-w-md">
                  Calls are recorded and analysed by our AI agent. Transcripts are handled per our{" "}
                  <Link to="/trust" className="text-zinc-400 hover:text-gold underline-offset-2 hover:underline">
                    Trust & Privacy
                  </Link>{" "}
                  policy.
                </p>
              </Reveal>
            </div>

            {/* Animated TA brand mark, lg+ only */}
            <div className="hidden lg:flex lg:col-span-5 items-center justify-center">
              <HeroBrandMark />
            </div>
          </div>

          <Reveal delay={420}>
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10 max-w-4xl" data-testid="hero-stats">
              {[
                { k: "4 levers", v: "Financials · Sales · Ops · Systems" },
                { k: "Advise · Build · Run", v: "End-to-end, one firm" },
                { k: "Senior", v: "Operators, not juniors" },
                { k: "Fixed-fee", v: "Outcomes, not timesheets" },
              ].map((m) => (
                <div key={m.v} className="border-l border-white/10 pl-4">
                  <div className="text-xl md:text-2xl font-display text-white">{m.k}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{m.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== INDUSTRIES STRIP ===================== */}
      <section className="border-y border-white/[0.05] bg-[#070707]" data-testid="industries-bar">
        <div className="container-x py-8 flex flex-col md:flex-row items-center md:justify-between gap-6">
          <div className="overline">Working with operators in</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-zinc-400 text-sm tracking-tight">
            {["Trade & Service", "Professional Services", "Industrial SMEs", "Healthcare", "Property", "Owner-operated SMEs"].map((l) => (
              <span key={l} className="hover:text-white transition-colors">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== AI INTAKE FLOW ===================== */}
      <section className="section-y" data-testid="ai-intake-flow">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="Start in minutes"
              title="A faster way to start the conversation."
              description="Our AI Strategist runs a focused 5 to 15-minute analysis on your goals. A senior consultant then prepares a detailed plan for your business and contacts you within 1 to 2 business days."
            />
          </Reveal>

          <div
            className="mt-16 relative grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/[0.05]"
            data-testid="ai-intake-grid"
          >
            {[
              {
                icon: Bot,
                step: "01",
                title: "AI Strategist call",
                meta: "5 to 15 minutes · Instant",
                desc: "Talk to our AI agent about your business: where you are, where you want to go, and what's in the way. Speaks your language, captures the context.",
              },
              {
                icon: FileText,
                step: "02",
                title: "Senior consultant review",
                meta: "Behind the scenes",
                desc: "A senior advisor at Toulios reviews the transcript, maps it against the four levers, and prepares a tailored growth plan for your business.",
              },
              {
                icon: PhoneCall,
                step: "03",
                title: "We contact you",
                meta: "Within 1 to 2 business days",
                desc: "You receive a written summary plus a recommended next step. If there's a fit, we book a deeper Strategy Call. If not, we tell you straight.",
              },
            ].map((s, i) => (
              <Reveal key={s.step} delay={i * 100}>
                <div
                  className="relative h-full bg-ink p-8 md:p-10"
                  data-testid={`ai-intake-step-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="overline">{s.step}</span>
                    <s.icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="mt-8 text-2xl md:text-3xl font-display font-light tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed text-[15px]">{s.desc}</p>
                  <div className="mt-8 flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold/80">
                    <Clock className="w-3.5 h-3.5" /> {s.meta}
                  </div>

                  {i < 2 && (
                    <span
                      aria-hidden
                      className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center bg-ink border border-gold/30"
                      data-testid={`ai-intake-connector-${i}`}
                    >
                      <ArrowRight className="w-3 h-3 text-gold" />
                    </span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <AIAgentCTA testId="ai-intake-cta" />
            <Link to="/contact" className="btn-ghost" data-testid="ai-intake-book">
              Or book a Strategy Call directly <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-4 text-center text-xs text-zinc-500 tracking-wide">
            No obligation. Confidential. Powered by Fireflies.
          </p>
          <p className="mt-2 text-center text-[11px] text-zinc-600 tracking-wide max-w-xl mx-auto">
            Calls are recorded and analysed by our AI agent. Transcripts handled per our{" "}
            <Link to="/trust" className="text-zinc-400 hover:text-gold underline-offset-2 hover:underline">
              Trust & Privacy
            </Link>{" "}
            policy.
          </p>
        </div>
      </section>

      {/* ===================== WHAT WE DO, FOUR LEVERS ===================== */}
      <section className="section-y" data-testid="four-levers">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="What we do"
              title="Four levers. One operating system."
              description="We work where most consultants stop and most agencies don't go. End-to-end advisory and execution across the four things that actually move performance."
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.05]">
            {LEVERS.map((l, i) => (
              <Reveal key={l.k} delay={i * 80} className="bg-ink p-8 md:p-10">
                <div className="overline">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-6 text-xl md:text-2xl font-display font-light tracking-tight">{l.k}</h3>
                <p className="mt-4 text-zinc-400 leading-relaxed text-[15px]">{l.v}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== DIVISIONS ===================== */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="divisions">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <SectionHeading
                eyebrow="The firm"
                title="Four divisions. One accountable team."
                description="Each division addresses one lever, but they're staffed and delivered as one integrated team. No vendor stitching."
              />
            </Reveal>
            <Link to="/what-we-do" className="btn-ghost self-start md:self-end" data-testid="home-divisions-link">
              How the firm is structured <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.05]">
            {DIVISIONS.map((d, i) => (
              <Reveal key={d.name} delay={i * 80}>
                <Link
                  to={d.to}
                  data-testid={`division-card-${i}`}
                  className={`block bg-ink p-8 md:p-10 h-full surface-hover ${d.flagship ? "relative" : ""}`}
                >
                  {d.flagship && (
                    <span
                      data-testid="division-flagship-badge"
                      className="absolute top-6 right-6 text-[10px] tracking-[0.22em] uppercase text-gold border border-gold/40 px-2 py-1"
                    >
                      Flagship
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="overline">{d.n}</span>
                    <d.icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="mt-8 text-2xl md:text-3xl font-display font-light tracking-tight">{d.name}</h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed">{d.pitch}</p>
                  <div className="mt-8 text-[11px] uppercase tracking-[0.2em] text-zinc-500">{d.note}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== THE ARCHITECTURE ===================== */}
      <section className="section-y" data-testid="architecture">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="The architecture"
              title="One firm. One system. One product."
              description="A deliberate, three-layer structure. The firm advises. The system is what we install. The OS is the software you sign into."
            />
          </Reveal>

          <div
            className="mt-16 relative grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/[0.05]"
            data-testid="architecture-grid"
          >
            {ARCHITECTURE.map((a, i) => (
              <Reveal key={a.label} delay={i * 100}>
                <div
                  className="relative h-full bg-ink p-8 md:p-10"
                  data-testid={`architecture-layer-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="overline">{a.role}</span>
                    <a.icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="mt-8 text-2xl md:text-3xl font-display font-light tracking-tight">
                    {a.label}
                  </h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed text-[15px]">{a.desc}</p>
                  <div className="mt-8 text-[11px] tracking-[0.22em] uppercase text-gold/80">
                    {a.badge}
                  </div>

                  {/* Connector arrow between cards (desktop only) */}
                  {i < ARCHITECTURE.length - 1 && (
                    <span
                      aria-hidden
                      className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center bg-ink border border-gold/30"
                      data-testid={`architecture-connector-${i}`}
                    >
                      <ArrowRight className="w-3 h-3 text-gold" />
                    </span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-zinc-500 max-w-xl mx-auto leading-relaxed">
              The firm runs the engagement. The engagement installs the system. The system runs on Growth OS. Built to be one continuous experience.
            </p>
          </div>

          {/* Grow-With-You callout */}
          <Reveal delay={120}>
            <div
              className="mt-12 max-w-3xl mx-auto border border-gold/30 bg-[#0a0a0a] p-6 md:p-8"
              data-testid="home-gwy-callout"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
                <div className="flex-1">
                  <div className="overline flex items-center gap-2">
                    <Handshake className="w-3.5 h-3.5" /> Or go performance-aligned
                  </div>
                  <h3 className="mt-3 font-display text-xl md:text-2xl tracking-tight">
                    <span className="text-white">Grow-With-You.</span>{" "}
                    <span className="text-zinc-400">Lower base. Share of the uplift.</span>
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                    A reduced fixed fee plus a performance share on incremental
                    gross profit, against a written baseline. We earn more when
                    you do.
                  </p>
                </div>
                <Link
                  to="/pricing#grow-with-you"
                  className="btn-outline-white whitespace-nowrap"
                  data-testid="home-gwy-cta"
                >
                  See the model <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== FLAGSHIP SPOTLIGHT ===================== */}
      <section className="section-y" data-testid="flagship-spotlight">
        <div className="container-x">
          <div className="relative overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#0d0d0d] to-[#070707] p-10 md:p-16 lg:p-20">
            <div
              aria-hidden
              className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full"
              style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.16), transparent 70%)" }}
            />
            <div aria-hidden className="absolute inset-x-0 top-0 h-px gold-line" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="overline flex items-center gap-2">
                  <Hammer className="w-3.5 h-3.5" /> Flagship engagement · the system layer
                </div>
                <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-display font-light tracking-tight text-balance">
                  Start with The Toulios Growth System.
                </h2>
                <p className="mt-6 max-w-2xl text-zinc-300 leading-relaxed text-pretty">
                  Six integrated components: modern website, lead capture, CRM
                  workflows, KPI dashboards, AI support and an outsourced
                  execution team. Installed in 60 days, powered by{" "}
                  <a href="#growth-os" className="text-gold underline-offset-4 hover:underline">Growth OS</a>,
                  and operated for you from week 8.
                </p>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/[0.05] max-w-2xl">
                  {[
                    { k: "60 days", v: "Audit → autopilot" },
                    { k: "Fixed-fee", v: "Quoted in writing" },
                    { k: "Run by us", v: "Outsourced execution" },
                  ].map((m) => (
                    <div key={m.v} className="bg-ink p-4">
                      <div className="font-display text-lg">{m.k}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">{m.v}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5 flex lg:justify-end">
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full sm:w-auto lg:w-full lg:max-w-xs">
                  <Link to="/growth-system" className="btn-gold justify-center" data-testid="flagship-cta">
                    Explore the Growth System <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/how-it-works" className="btn-outline-white justify-center">
                    See the build process
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== MEET GROWTH OS ===================== */}
      <section
        id="growth-os"
        className="section-y bg-[#070707] border-y border-white/[0.05] scroll-mt-24"
        data-testid="growth-os"
      >
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5" /> The software product
            </div>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-display font-light tracking-tight text-balance">
              Meet Toulios <span className="text-gold">Growth OS</span>.
            </h2>
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl">
              The Growth System is what we install. <span className="text-white">Growth OS</span>{" "}
              is what you sign into. A single, branded platform that runs your
              website, CRM, dashboards, automations and AI assistant as one
              connected operating system.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-zinc-300" data-testid="os-highlights">
              {OS_HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/growth-os" className="btn-gold" data-testid="os-cta-tour">
                Take a guided tour <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/growth-system" className="btn-outline-white" data-testid="os-cta-system">
                See the system that powers it
              </Link>
            </div>
          </div>

          {/* OS module grid */}
          <div className="lg:col-span-7">
            <div className="relative">
              <div
                aria-hidden
                className="absolute -inset-px bg-gradient-to-br from-gold/15 via-transparent to-transparent pointer-events-none"
              />
              <div
                className="relative grid grid-cols-2 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]"
                data-testid="os-modules"
              >
                {OS_MODULES.map((m) => (
                  <div
                    key={m.id}
                    className="bg-ink p-6 md:p-8 transition-colors hover:bg-[#101010]"
                    data-testid={`os-module-${m.id}`}
                  >
                    <m.icon className="w-5 h-5 text-gold" />
                    <div className="mt-5 font-display text-lg tracking-tight">{m.name}</div>
                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-6 text-xs text-zinc-500 text-center tracking-wide">
              One login. Six modules. Branded to your business.
            </p>
          </div>
        </div>
      </section>

      {/* ===================== HOW WE WORK ===================== */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="how-we-work">
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="How we work"
              title="Diagnose. Design. Deliver. Run."
              description="The same four-stage rhythm applies to every engagement, whether it&rsquo;s the flagship Growth System or a multi-division retained partnership."
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.05]">
            {PROCESS.map((p, i) => (
              <Reveal key={p.title} delay={i * 100} className="bg-ink p-8 md:p-10">
                <div className="overline">{p.n}</div>
                <h4 className="mt-6 text-xl md:text-2xl font-display font-light tracking-tight">{p.title}</h4>
                <p className="mt-4 text-zinc-400 leading-relaxed text-[15px]">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHY US ===================== */}
      <section className="section-y" data-testid="why-us">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Why Toulios
            </div>
            <h2 className="mt-6 font-display font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl text-balance">
              Senior advisors. Operating discipline. Accountable to outcomes.
            </h2>
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl">
              The questions established operators wrestle with don&rsquo;t resolve in a slide deck. They resolve when senior people get under the bonnet, design the system, and stay involved while it&rsquo;s installed. That&rsquo;s the work we do.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.05]">
            {PROOF.map((p, i) => (
              <Reveal key={p.v} delay={i * 80} className="bg-ink p-8 md:p-10">
                <div className="overline">{p.k}</div>
                <p className="mt-4 text-zinc-200 leading-relaxed">{p.v}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CASE STUDIES PREVIEW ===================== */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="case-preview">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <SectionHeading
                eyebrow="Proof"
                title="Outcomes, not optics."
                description="Anonymised. Indicative. Full case studies and references available on request, under mutual NDA."
              />
            </Reveal>
            <Link to="/case-studies" className="btn-ghost self-start md:self-end" data-testid="home-cases-link">
              All case studies <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CASE_PREVIEWS.map((c, i) => (
              <Reveal key={c.title} delay={i * 100}>
                <div className="surface surface-hover p-8 md:p-10 h-full flex flex-col" data-testid={`case-preview-${i}`}>
                  <div className="overline">{c.sector}</div>
                  <h4 className="mt-6 text-xl md:text-2xl font-display font-light tracking-tight text-balance">{c.title}</h4>
                  <p className="mt-5 text-sm text-zinc-400 leading-relaxed flex-1">{c.note}</p>
                  <div className="mt-8 text-xs uppercase tracking-[0.2em] text-zinc-500">Anonymised · indicative</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="section-y" data-testid="home-faq">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="The honest answers."
              description="If your question isn&rsquo;t here, ask it on the call. We&rsquo;ll tell you straight."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="home-faq" />
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
